import React from 'react';

/**
 * Logo — the canonical EquiTrack brandmark.
 *
 * Renders the official PNG mark sourced from /public/logo.png so the
 * marketing site, app sidebar, login/register, success page, and
 * mobile drawer all share one visual asset. Replaces the previous
 * `TrendingUp + "EquiTrack"` text composition.
 *
 * Props:
 *   - size: 'sm' | 'md' | 'lg' (default 'md') — controls the height.
 *   - withText: boolean (default true on desktop, false on tight rails).
 *                Passed `false` when the wordmark is already shown in
 *                the parent layout (e.g. mobile-drawer header).
 *   - className: optional extra Tailwind classes for the wrapper.
 *
 * Why a single component? The new logo asset includes the "equitrack.in"
 * wordmark baked in, so we never split the icon from the wordmark —
 * one image, sized consistently, reads as a single brand entity.
 */
export const Logo = ({ size = 'md', alt = 'EquiTrack', className = '', testid }) => {
  const sizeMap = {
    xs: 'h-6',
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12',
    xl: 'h-16',
  };
  const cls = sizeMap[size] || sizeMap.md;
  return (
    <img
      src="/logo.png"
      alt={alt}
      className={`${cls} w-auto select-none ${className}`}
      draggable={false}
      data-testid={testid || 'app-logo'}
      // Iter-196 — the source PNG ships with a baked-in WHITE background.
      // We don't have an image editor in this environment, but
      // ``mix-blend-mode: multiply`` makes white pixels effectively
      // transparent against any light surface (cream/white/blue tinted
      // tile backgrounds the app uses), letting the brand mark blend
      // seamlessly with every parent surface — no source-asset edit
      // required. ``isolation: isolate`` is intentionally NOT set so
      // the blend reaches the actual page background.
      style={{ mixBlendMode: 'multiply' }}
      width="auto"
      height="auto"
    />
  );
};

export default Logo;
